import { apiFetch } from './api.js';
import type { CreditSummary } from '../session/types.js';

export type OrderStatus =
  | 'submitted'
  | 'in_build'
  | 'in_review'
  | 'live'
  | 'extended'
  | 'expired'
  | 'cancelled';

export type OrderType = 'site_build' | 'content_refresh' | 'year_extension';

export type CreditsState = 'reserved' | 'committed' | 'released';

/** Round-1 link targets. Still stored, no longer collected by any form. */
export interface LinkTarget {
  url: string;
  anchor?: string;
}

/** Link out to the target, or name it throughout and link to nothing. */
export type LinkMode = 'link_out' | 'mentions_only';

/** One outgoing link. The home row is derived by the server from the main URL. */
export interface IntakeLink {
  role: 'home' | 'secondary';
  url: string;
  wording: string | null;
}

/** The sitewide footer's details, as stored. Never the pasted map HTML. */
export interface FooterNap {
  businessName: string | null;
  phone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  region: string | null;
  postcode: string | null;
  country: string | null;
  mapsEmbedUrl: string | null;
}

/** The footer as a form sends it: the map arrives as a pasted snippet. */
export interface FooterDetailsInput {
  businessName?: string;
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  region?: string;
  postcode?: string;
  country?: string;
  mapsEmbed?: string;
}

export interface ClientProfile {
  id: string;
  businessName: string;
  services: string[];
  locations: string[];
  /** The one town or area intake v2 asks for: the head of `locations`. */
  targetLocation: string | null;
  websiteUrl: string | null;
  gbpUrl: string | null;
  usps: string | null;
  targetQueries: string[];
  brandConstraints: string | null;
  linkTargets: LinkTarget[];
  footerBlock: string | null;
  avoidNotes: string | null;
  footerNap: FooterNap | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * What the project form sends.
 *
 * The new field set only: target information and the local section. The
 * round-1 fields are absent on purpose — absent means "leave that column
 * exactly as it is", so editing a project never wipes what it already holds.
 */
export interface ClientProfileInput {
  businessName: string;
  websiteUrl: string;
  targetLocation: string;
  footer: FooterDetailsInput | null;
}

export interface OrderTypeOption {
  type: OrderType;
  label: string;
  description: string;
  /** The cost of one year of this type. */
  credits: number;
  requiresSite: boolean;
  /** The most years one order of this type may carry. */
  maxYears: number;
  /** What each further year adds. Flat — no term discount (ruling C-2/C-3). */
  creditsPerYear: number;
}

export interface Site {
  id: string;
  clientProfileId: string;
  /** The project this site belongs to, so renewal can start from the site. */
  businessName: string;
  liveUrl: string | null;
  customDomain: string | null;
  goLiveDate: string | null;
  liveUntil: string | null;
  /**
   * The pages carrying the client's links - D-14 Item 9.
   *
   * Empty for every site delivered before migration 0018, which is the truth
   * about them rather than a gap: nobody recorded it at the time.
   */
  linkUrls: string[];
}

/**
 * The intake as it was submitted, frozen on the order — walkthrough finding
 * W-1. Null for orders placed before this existed.
 *
 * **Two shapes, both current.** Round-1 orders hold `…V1`; orders placed since
 * intake v2 hold `…V2`. Stored snapshots are never migrated or rewritten, so
 * the operator page renders whichever it is handed. `version` discriminates,
 * and its absence means round 1.
 */
export interface OrderIntakeSnapshotV1 {
  version?: undefined;
  businessName: string;
  websiteUrl: string | null;
  gbpUrl: string | null;
  services: string[];
  locations: string[];
  linkTargets: LinkTarget[];
  usps: string | null;
  brandConstraints: string | null;
  avoidNotes: string | null;
  footerBlock: string | null;
  targetQueries: string[];
  capturedAt: string;
}

export interface OrderIntakeSnapshotV2 {
  version: 2;
  targetName: string;
  mainUrl: string | null;
  linkMode: LinkMode;
  /** At most three. Empty for a mentions-only build. */
  links: IntakeLink[];
  local: {
    targetLocation: string | null;
    footer: FooterNap | null;
  };
  capturedAt: string;
}

export type OrderIntakeSnapshot = OrderIntakeSnapshotV1 | OrderIntakeSnapshotV2;

/** Whether a stored snapshot is the intake v2 shape. */
export function isIntakeV2(snapshot: OrderIntakeSnapshot): snapshot is OrderIntakeSnapshotV2 {
  return snapshot.version === 2;
}

export interface Order {
  id: string;
  clientProfileId: string;
  orderType: OrderType;
  creditsCost: number;
  creditsState: CreditsState;
  status: OrderStatus;
  operatorNote: string | null;
  siteId: string | null;
  intakeSnapshot: OrderIntakeSnapshot | null;
  /** Years of the site being live that this order pays for. */
  years: number;
  createdAt: string;
  updatedAt: string;
}

export interface OrderListItem extends Order {
  businessName: string;
}

export interface TimelineEvent {
  id: string;
  fromStatus: OrderStatus | null;
  toStatus: OrderStatus;
  note: string | null;
  createdAt: string;
}

export interface Deliverable {
  id: string;
  orderId: string;
  kind: 'build_report' | 'client_report' | 'other';
  label: string;
  contentType: string | null;
  sizeBytes: number | null;
  filename: string | null;
  createdAt: string;
}

export interface OrderDetail {
  order: Order;
  timeline: TimelineEvent[];
  deliverables: Deliverable[];
  /** The delivered site and its handover - D-14 Item 9.3. Null until it is live. */
  site: Site | null;
}

/* ------------------------------------------------------------------ calls --- */

export function fetchClientProfiles(): Promise<{ clientProfiles: ClientProfile[] }> {
  return apiFetch('/api/client-profiles');
}

export function fetchClientProfile(
  id: string,
): Promise<{ clientProfile: ClientProfile; orderCount: number }> {
  return apiFetch(`/api/client-profiles/${id}`);
}

export function createClientProfile(
  input: Partial<ClientProfileInput>,
): Promise<{ clientProfile: ClientProfile }> {
  return apiFetch('/api/client-profiles', { method: 'POST', body: input });
}

export function updateClientProfile(
  id: string,
  input: Partial<ClientProfileInput>,
): Promise<{ clientProfile: ClientProfile }> {
  return apiFetch(`/api/client-profiles/${id}`, { method: 'PATCH', body: input });
}

export function fetchOrderTypes(): Promise<{ orderTypes: OrderTypeOption[] }> {
  return apiFetch('/api/order-types');
}

export function fetchSites(): Promise<{ sites: Site[] }> {
  return apiFetch('/api/sites');
}

export interface AgencyRenewalSite {
  siteId: string;
  clientProfileId: string;
  businessName: string;
  liveUrl: string | null;
  liveUntil: string;
  daysRemaining: number;
}

/** The agency's own sites whose paid year is running out. */
export function fetchAgencyRenewals(
  withinDays = 45,
): Promise<{ withinDays: number; sites: AgencyRenewalSite[] }> {
  return apiFetch(`/api/renewals?withinDays=${withinDays}`);
}

export function fetchOrders(): Promise<{ orders: OrderListItem[] }> {
  return apiFetch('/api/orders');
}

export function fetchOrder(id: string): Promise<OrderDetail> {
  return apiFetch(`/api/orders/${id}`);
}

/**
 * Order intake v2 — what the order form submits.
 *
 * Mirrors the server's `orderIntakeSchema`; the server is the authority and
 * re-validates every field, including the link cap and the map embed.
 *
 * Two answers are required — `targetName` and `mainUrl` — and nothing else is.
 * There is still no field for a home page link *row*: the server builds that row
 * itself, which is what makes "exactly one home page link" impossible to get
 * wrong from here. `homeLinkUrl` says where that one row points, and omitting it
 * means the main URL.
 */
export interface OrderIntake {
  targetName: string;
  mainUrl: string;
  linkMode: LinkMode;
  /** Where the home page link points. Omitted means `mainUrl`. */
  homeLinkUrl?: string;
  homeLinkWording?: string;
  secondaryLinks?: { url: string; wording?: string }[];
  local?: {
    targetLocation?: string;
    footer: FooterDetailsInput | null;
  };
}

export function placeOrder(input: {
  /** Omit to have the project created from `intake` (A-1). */
  clientProfileId?: string;
  intake?: OrderIntake;
  orderType: OrderType;
  siteId?: string;
  years?: number;
}): Promise<{ order: Order; credits: CreditSummary }> {
  return apiFetch('/api/orders', { method: 'POST', body: input });
}

/**
 * Renews one or more sites — the site-first flow (C-1).
 *
 * All or nothing: the batch is priced and refused together, so an agency
 * ticking five sites with credits for three ends up with none rather than an
 * arbitrary three.
 */
export function extendSites(input: {
  siteIds: string[];
  years: number;
}): Promise<{ orders: Order[]; credits: CreditSummary }> {
  return apiFetch('/api/extensions', { method: 'POST', body: input });
}

/**
 * Cancels an order the agency placed. Allowed at `submitted` only — the server
 * refuses anything later, and this is the one agency call that moves an order.
 */
export function cancelOrder(
  orderId: string,
  reason?: string,
): Promise<{ order: Order; timeline: TimelineEvent[]; credits: CreditSummary }> {
  return apiFetch(`/api/orders/${orderId}/cancel`, {
    method: 'POST',
    body: reason ? { reason } : {},
  });
}

/** Downloads go through the authenticated API route, never a public URL. */
export function deliverableDownloadUrl(orderId: string, deliverableId: string): string {
  return `/api/orders/${orderId}/deliverables/${deliverableId}/download`;
}
